import { dedent } from 'ts-dedent'
import {
  ColumnDataType,
  Provider,
  QueryAndOutputs,
  WorkflowCodeType,
  WorkflowComponentCodeParams,
  WorkflowComponentParamType
} from '../types'
import { SingleOutputWorkflowComponent } from './common/singleoutputcomponent'

// get Pastel - qualitative color list (longest)
// that is the default used in Builder
// get it from cartoColors that is a copy of the one:
// https://github.com/CartoDB/cloud-native/blob/main/workspace-www/src/features/builder/ui/theme/cartoColors.ts
import { cartoQUAColors } from './palettes/cartoColors'
const pastelPaletteStr = cartoQUAColors.find((palette) => palette.name === 'Pastel')?.colors as string[]
const DEFAULT_COLOR = '#808080'

class HexColorGenerator extends SingleOutputWorkflowComponent {
  constructor() {
    super(
      {
        name: 'native.hexcolorgenerator',
        group: 'Data Preparation',
        title: 'Hex Color Generator',
        description: `
          This component creates hex color for each different value of an input string column.
          Column values as NULL will be associated to grey value.`,
        starred: false
      },
      [
        {
          name: 'source',
          type: WorkflowComponentParamType.InputTable,
          title: 'Main table'
        },
        {
          name: 'column',
          type: WorkflowComponentParamType.Column,
          title: 'Column with category values',
          parent: 'source',
          dataType: [ColumnDataType.String, ColumnDataType.Number]
        }
      ]
    )
  }

  async getCode({
    workflow,
    params,
    connection,
    componentName,
    codeType
  }: WorkflowComponentCodeParams): Promise<QueryAndOutputs> {
    const dryRun = codeType === WorkflowCodeType.DryRun
    const outputTablename = connection.getOutputTableName(workflow.id, componentName, params, 'result', codeType)
    const preparedParams = this._prepareParameters(params)
    const quotedSource: string = connection.quoteName(preparedParams.source as string)
    const tempSchema = connection.quoteName(connection.tempLocation)
    const newColumnName = preparedParams.column + '_hex_color'

    // JS and PY portion nested in the SQL to calculate the hex color
    // inspired by https://stackoverflow.com/questions/2353211/hsl-to-rgb-color-conversion
    const jsHSLtoRGB = dedent`
      function hueToRgb(p, q, t) {
        if (t < 0) t += 1;
        if (t > 1) t -= 1;
        if (t < 1/6) return p + (q - p) * 6 * t;
        if (t < 1/2) return q;
        if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
        return p;
      };

      let r, g, b;
      if (S === 0) {
        r = g = b = L; // achromatic
      } else {
        const q = L < 0.5 ? L * (1 + S) : L + S - L * S;
        const p = 2 * L - q;
        r = hueToRgb(p, q, H + 1/3);
        g = hueToRgb(p, q, H);
        b = hueToRgb(p, q, H - 1/3);
      }
      r = Math.round(r*255);
      g = Math.round(g*255);
      b = Math.round(b*255);

      // build and return hex color
      const RR = r.toString(16).toUpperCase().padStart(2, '0')
      const GG = g.toString(16).toUpperCase().padStart(2, '0')
      const BB = b.toString(16).toUpperCase().padStart(2, '0')
      return '#' + RR + GG + BB;
    `
    const pyHSLtoRGB = dedent`
      def hueToRgb(pp, qq, tt):
        if (tt < 0): tt += 1
        if (tt > 1): tt -= 1
        if (tt < 1./6): return pp + (qq - pp) * 6 * tt
        if (tt < 1./2): return qq
        if (tt < 2./3): return pp + (qq - pp) * (2./3 - tt) * 6
        return pp

      r = g = b = 0
      if (S == 0):
        r = g = b = L # achromatic
      else:
        q = (L*(1+S)) if (L < 0.5) else (L+S - L*S)
        p = (2. * L) - q
        r = hueToRgb(p, q, H + 1./3)
        g = hueToRgb(p, q, H)
        b = hueToRgb(p, q, H - 1./3)

      r = int(round(r*255))
      g = int(round(g*255))
      b = int(round(b*255))

      # build and return hex color
      RR = hex(r)[2:].upper().zfill(2)
      GG = hex(g)[2:].upper().zfill(2)
      BB = hex(b)[2:].upper().zfill(2)

      return '#{RR}{GG}{BB}'.format(RR=RR, GG=GG, BB=BB)
    `

    let query = ''
    if (dryRun) {
      query = connection.getCreateTempTableQuery(
        outputTablename,
        dedent`
          SELECT
            t.*,
            '${DEFAULT_COLOR}' AS ${newColumnName}
          FROM
            ${quotedSource} t
        `,
        codeType,
        null
      )
    } else {
      switch (connection.type) {
        case Provider.BigQuery: {
          query = dedent`
          CREATE TEMP FUNCTION HSLtoRGB(H FLOAT64, S FLOAT64, L FLOAT64)
          RETURNS STRING
          LANGUAGE js
          AS """
            ${jsHSLtoRGB}
          """;

          ${connection.getCreateTempTableQuery(
            outputTablename,
            dedent`
              WITH categories AS (
                SELECT DISTINCT
                    CAST(${preparedParams.column} AS STRING) as category
                FROM ${quotedSource}
                WHERE ${preparedParams.column} IS NOT NULL
              ),
              categoriesAndIds AS (
                SELECT
                  ROW_NUMBER() OVER (ORDER BY RAND()) as _id,
                  category
                FROM categories
              ),
              countAndStep AS (
                SELECT
                  COUNT(_id) as nCategories,
                  (360.0 / COUNT(_id)) as step
                FROM categoriesAndIds
              ),
              lookupTable AS (
                SELECT
                  category,
                  CASE
                    WHEN nCategories <= ${pastelPaletteStr.length} THEN
                      ${JSON.stringify(pastelPaletteStr)}[OFFSET(_id - 1)]
                    ELSE
                      HSLtoRGB((_id - 1) * step / 360, 1, 0.75)
                  END
                  AS ${newColumnName}
                FROM
                  categoriesAndIds,
                  countAndStep
                ORDER BY category
              )
              SELECT
                s.*,
                COALESCE(l.${newColumnName}, '${DEFAULT_COLOR}') AS ${newColumnName}
              FROM
                ${quotedSource} s
              LEFT JOIN lookupTable l
              ON CAST(s.${preparedParams.column} AS STRING) = l.category
            `,
            codeType,
            null
          )}
        `
          break
        }
        case Provider.Snowflake: {
          query = dedent`
          BEGIN
            CREATE OR REPLACE FUNCTION ${tempSchema}.HSLtoRGB(H DOUBLE, S DOUBLE, L DOUBLE)
            RETURNS STRING
            LANGUAGE JAVASCRIPT
            AS '
              ${jsHSLtoRGB.replace(/'/g, "''")}
            ';

            ${connection.getCreateTempTableQuery(
              outputTablename,
              dedent`
              WITH categories AS (
                SELECT DISTINCT
                    TRY_CAST(${preparedParams.column} AS STRING) as category
                FROM ${quotedSource}
                WHERE ${preparedParams.column} IS NOT NULL
              ),
              categoriesAndIds AS (
                SELECT
                  ROW_NUMBER() over (ORDER BY RANDOM()) as _id,
                  category
                FROM categories
              ),
              countAndStep AS (
                SELECT
                  COUNT(_id) as nCategories,
                  (360.0 / COUNT(_id)) as step
                FROM categoriesAndIds
              ),
              lookupTable AS (
                SELECT
                  category,
                  CASE
                    WHEN nCategories <= ${pastelPaletteStr.length} THEN
                      GET(
                        ARRAY_CONSTRUCT(
                          ${JSON.stringify(pastelPaletteStr).replace(/"/g, "'")}
                        ),
                        _id
                      )
                    ELSE
                    ${tempSchema}.HSLtoRGB((_id - 1) * step / 360, 1, 0.75)
                  END
                  AS ${newColumnName}
                FROM
                  categoriesAndIds,
                  countAndStep
                ORDER BY category
              )
              SELECT
                s.*,
                COALESCE(l.${newColumnName}, '${DEFAULT_COLOR}') AS ${newColumnName}
              FROM
                ${quotedSource} s
              LEFT OUTER JOIN lookupTable l
              ON TRY_CAST(s.${preparedParams.column} AS STRING) = l.category
              `,
              codeType,
              null
            )}
            DROP FUNCTION ${tempSchema}.HSLtoRGB (DOUBLE, DOUBLE, DOUBLE);
          END;
        `
          break
        }
        case Provider.Redshift: {
          query = dedent`
          BEGIN
            CREATE OR REPLACE FUNCTION ${tempSchema}.HSLtoRGB (H float, S float, L float)
            RETURNS VARCHAR
            STABLE
            AS $HSLtoRGB$
              ${pyHSLtoRGB}
            $HSLtoRGB$ LANGUAGE plpythonu;

            ${connection.getCreateTempTableQuery(
              outputTablename,
              dedent`
                WITH
                categories AS (
                  SELECT DISTINCT
                    ${preparedParams.column}::varchar as category
                  FROM ${quotedSource}
                  WHERE ${preparedParams.column} IS NOT NULL
                ),
                categoriesAndIds AS (
                  SELECT
                    ROW_NUMBER() over (ORDER BY RANDOM()) as _id,
                    category
                  FROM categories
                ),
                countAndStep AS (
                  SELECT
                    COUNT(_id) as nCategories,
                    (360.0 / COUNT(_id)) as step
                  FROM categoriesAndIds
                ),
                lookupTable AS (
                  SELECT
                    category,
                    CASE
                      WHEN nCategories <= ${pastelPaletteStr.length} THEN
                      JSON_EXTRACT_ARRAY_ELEMENT_TEXT(
                        '${JSON.stringify(pastelPaletteStr)}',
                        (_id - 1)::integer
                      )
                      ELSE
                        ${tempSchema}.HSLtoRGB((_id - 1) * step / 360, 1, 0.75)
                    END
                    AS ${newColumnName}
                  FROM
                    categoriesAndIds,
                    countAndStep
                  ORDER BY category
                )
                SELECT
                  s.*,
                  COALESCE(l.${newColumnName}, '${DEFAULT_COLOR}') AS ${newColumnName}
                FROM
                  ${quotedSource} s
                LEFT JOIN lookupTable l
                ON s.${preparedParams.column}::varchar = l.category
              `,
              codeType,
              null
            )}
            DROP FUNCTION ${tempSchema}.HSLtoRGB(float, float, float);
          END;`
          break
        }
        case Provider.Postgres: {
          query = dedent`
          BEGIN
            CREATE OR REPLACE FUNCTION ${tempSchema}.HSLtoRGB(H float, S float, L float)
            RETURNS VARCHAR
            STABLE
            AS $HSLtoRGB$
              ${jsHSLtoRGB
                .replace(/H\)/g, 'h)')
                .replace(/ H /g, ' h ')
                .replace(/S\)/g, 's)')
                .replace(/\(S/g, '(s')
                .replace(/S;/g, 's;')
                .replace(/ S /g, ' s ')
                .replace(/ L /g, ' l ')
                .replace(/ L;/g, ' l;')}
            $HSLtoRGB$ LANGUAGE plv8;

            ${connection.getCreateTempTableQuery(
              outputTablename,
              dedent`
                WITH
                categories AS (
                  SELECT DISTINCT
                    ${preparedParams.column}::varchar as category
                  FROM ${quotedSource}
                  WHERE ${preparedParams.column} IS NOT NULL
                ),
                categoriesAndIds AS (
                  SELECT
                    ROW_NUMBER() over (ORDER BY RANDOM()) as _id,
                    category
                  FROM categories
                ),
                countAndStep AS (
                  SELECT
                    COUNT(_id) as nCategories,
                    (360.0 / COUNT(_id)) as step
                  FROM categoriesAndIds
                ),
                lookupTable AS (
                  SELECT
                    category,
                    CASE
                      WHEN nCategories <= ${pastelPaletteStr.length} THEN
                        ('${JSON.stringify(pastelPaletteStr)}'::json->(_id - 1)::integer)::varchar
                      ELSE
                        ${tempSchema}.HSLtoRGB((_id - 1) * step / 360, 1, 0.75)
                    END
                    AS ${newColumnName}
                  FROM
                    categoriesAndIds,
                    countAndStep
                  ORDER BY category
                )
                SELECT
                  s.*,
                  COALESCE(l.${newColumnName}, '${DEFAULT_COLOR}') AS ${newColumnName}
                FROM
                  ${quotedSource} s
                LEFT JOIN lookupTable l
                ON s.${preparedParams.column}::varchar = l.category
              `,
              codeType,
              null
            )}
            DROP FUNCTION IF EXISTS ${tempSchema}.HSLtoRGB(float, float, float);
          END;`
          break
        }
        case Provider.DatabricksRest: {
          const select = dedent`
        WITH categories AS (
          SELECT /*+ REPARTITION(40) */
            DISTINCT CAST(${preparedParams.column} AS STRING) as category
          FROM ${quotedSource}
          WHERE ${preparedParams.column} IS NOT NULL
        ),
        categoriesAndIds AS (
          SELECT /*+ REPARTITION(40) */
            ROW_NUMBER() OVER (ORDER BY RAND()) as _id,
            category
          FROM categories
        ),
        countAndStep AS (
          SELECT /*+ REPARTITION(40) */
            COUNT(_id) as nCategories,
            (360.0 / COUNT(_id)) as step
          FROM categoriesAndIds
        ),
        lookupTable AS (
          SELECT /*+ REPARTITION(40) */
            category,
            CASE
              WHEN nCategories <= ${pastelPaletteStr.length} THEN
                ARRAY(${JSON.stringify(pastelPaletteStr.join(', '))})[_id - 1]
              ELSE
                (${HSLtoRGB('(_id - 1) * step / 360', '1', '0.75')})
            END
            AS ${newColumnName}
          FROM
            categoriesAndIds,
            countAndStep
          ORDER BY category
        )
        SELECT /*+ REPARTITION(40) */
          s.*,
          COALESCE(l.${newColumnName}, '${DEFAULT_COLOR}') AS ${newColumnName}
        FROM
          ${quotedSource} s
        LEFT JOIN lookupTable l
        ON CAST(s.${preparedParams.column} AS STRING) = l.category`
          query = connection.getCreateTempTableQuery(outputTablename, select, codeType, null)

          break
        }
      }
    }
    return {
      query,
      outputs: { result: outputTablename }
    }
  }

  supportedProviders(): Provider[] {
    return [Provider.BigQuery, Provider.Postgres, Provider.Redshift, Provider.Snowflake, Provider.DatabricksRest]
  }
}

function HSLtoRGB(H, S, L) {
  return dedent`WITH input_values AS (
  SELECT /*+ REPARTITION(40) */
    ${H} AS H,  -- Hue
    ${S} AS S,  -- Saturation
    ${L} AS L   -- Lightness
),
-- Implement hueToRgb as a SQL expression
hue_to_rgb AS (
  SELECT /*+ REPARTITION(40) */
    *,
    (CASE
       WHEN H + 1/3 < 0 THEN H + 1/3 + 1
       WHEN H + 1/3 > 1 THEN H + 1/3 - 1
       ELSE H + 1/3
     END) AS ht1,
    (CASE
       WHEN H < 0 THEN H + 1
       WHEN H > 1 THEN H - 1
       ELSE H
     END) AS ht2,
    (CASE
       WHEN H - 1/3 < 0 THEN H - 1/3 + 1
       WHEN H - 1/3 > 1 THEN H - 1/3 - 1
       ELSE H - 1/3
     END) AS ht3
  FROM input_values
),
-- Calculate p and q values based on lightness and saturation
pq_values AS (
  SELECT /*+ REPARTITION(40) */
    *,
    CASE
      WHEN L < 0.5 THEN L * (1 + S)
      ELSE L + S - L * S
    END AS q,
    2 * L -
      CASE
        WHEN L < 0.5 THEN L * (1 + S)
        ELSE L + S - L * S
      END AS p
  FROM hue_to_rgb
),
-- Convert HSL to RGB
final_rgb AS (
  SELECT /*+ REPARTITION(40) */
    *,
    ROUND(
      CASE
        WHEN S = 0 THEN L * 255 -- achromatic
        ELSE
          CASE
            WHEN ht1 < 1/6 THEN p + (q - p) * 6 * ht1
            WHEN ht1 < 1/2 THEN q
            WHEN ht1 < 2/3 THEN p + (q - p) * (2/3 - ht1) * 6
            ELSE p
          END * 255
      END
    ) AS r,
    ROUND(
      CASE
        WHEN S = 0 THEN L * 255 -- achromatic
        ELSE
          CASE
            WHEN ht2 < 1/6 THEN p + (q - p) * 6 * ht2
            WHEN ht2 < 1/2 THEN q
            WHEN ht2 < 2/3 THEN p + (q - p) * (2/3 - ht2) * 6
            ELSE p
          END * 255
      END
    ) AS g,
    ROUND(
      CASE
        WHEN S = 0 THEN L * 255 -- achromatic
        ELSE
          CASE
            WHEN ht3 < 1/6 THEN p + (q - p) * 6 * ht3
            WHEN ht3 < 1/2 THEN q
            WHEN ht3 < 2/3 THEN p + (q - p) * (2/3 - ht3) * 6
            ELSE p
          END * 255
      END
    ) AS b
  FROM pq_values
),
-- Convert RGB values to hexadecimal format
hex_color AS (
  SELECT /*+ REPARTITION(40) */
    *,
    LPAD(UPPER(HEX(CAST(r AS INT))), 2, '0') AS RR,
    LPAD(UPPER(HEX(CAST(g AS INT))), 2, '0') AS GG,
    LPAD(UPPER(HEX(CAST(b AS INT))), 2, '0') AS BB
  FROM final_rgb
)
-- Build the final hex color code
SELECT /*+ REPARTITION(40) */
  '#' || RR || GG || BB AS hex_color
FROM
  hex_color`
}

export { HexColorGenerator }
